import {Component, Inject, OnInit, signal, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Globals} from '../../../../globals';
import {ARService} from '../../ar.service';
import {EnumValidValueItemModel} from '../../../../models/enum/enum-valid-value-item.model';
import {DataARCreditDebitReasonModel} from '../../../../models/accountsReceivable/data-ar-credit-debit-reason.model';
import {TdoTableV2Component} from '../../../../tdo-controls/common/tdo-table-v2/tdo-table-v2.component';
import {MatTable} from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { UiService } from 'src/app/services/ui.service';

@Component({
  standalone: false,
  selector: 'app-ar-debit-credit-reasons',
  templateUrl: './ar-debit-credit-reasons.component.html',
  styleUrls: ['./ar-debit-credit-reasons.component.scss']
})
export class ArDebitCreditReasonsComponent implements OnInit {

  @ViewChild(TdoTableV2Component) tdoTable: TdoTableV2Component;
  @ViewChild(MatTable) table!: MatTable<any>;

  /** Signal — integrates with v21 CD model and avoids NG0100 from async checkNoChanges pass. */
  readonly isLoading = signal<boolean>(true);
  canSaveValue: boolean = false;
  enumAdjustmentReasons: EnumValidValueItemModel[] = [];
  creditDebitReasons: DataARCreditDebitReasonModel[] = [];
  creditDebitReasonsTable: DataARCreditDebitReasonModel[] = [];
  creditDebitReasonsRemoved: DataARCreditDebitReasonModel[] = [];
  LOCID: number;
  showCreditReasons: boolean = false;
  showDebitReasons: boolean = false;
  enumCreditDebitTypeCredit: string = '00000538-1000-0000-0000-000000000001';
  enumCreditDebitTypeDebit: string = '00000538-1000-0000-0000-000000000002';

  columnsToDisplayTopRow = ['Reason', 'enumCreditDebitTypeID', 'isExcludeFromAdjRec', 'CreditDebitReasonID'];

  constructor(
    public dialogRef: MatDialogRef<ArDebitCreditReasonsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly arService: ARService,
    public readonly globals: Globals,
    private readonly dialog: MatDialog,
    private readonly uiService: UiService
  ) {  }

  // the same flag excludes credit reasons from adjusted production and debit reasons from adjusted receipts
  get excludeFromAdjustedLabel(): string {
    return this.showCreditReasons ? 'Exclude from Adjusted Production' : 'Exclude from Adjusted Receipts';
  }

  ngOnInit() {
    this.LOCID = parseInt(localStorage.getItem('LOCID'));

    if (this.data.reasonType === 'credit') {
      this.showCreditReasons = true;
    }
    else if (this.data.reasonType === 'debit' || this.data.reasonType === 'void-payment') {
      this.showDebitReasons = true;
    }

    this.init();
  }

  init() {

    this.isLoading.set(true);
    const promises: any[] = [
      firstValueFrom(this.arService.getDebitCreditReasons(this.LOCID, true))
    ];
    if (this.showDebitReasons) {
      promises.push(firstValueFrom(this.arService.getEnumValidValueItemsByValidValueName('enumARReasonDebitAdjustment')));
    }
    else if (this.showCreditReasons) {
      promises.push(firstValueFrom(this.arService.getEnumValidValueItemsByValidValueName('enumARReasonCreditAdjustment')));
    }

    Promise.all(promises).then((results: any[]) => {
      this.creditDebitReasons = results[0];
      this.enumAdjustmentReasons = results[1];
      // filter by type
      if (this.showCreditReasons) {
        this.creditDebitReasonsTable = this.creditDebitReasons.filter(a => a.enumCreditDebitTypeID === this.enumCreditDebitTypeCredit);
      } else {
        this.creditDebitReasonsTable = this.creditDebitReasons.filter(a => a.enumCreditDebitTypeID === this.enumCreditDebitTypeDebit);
      }
      this.updateCanSave();
      this.isLoading.set(false);
    }).catch((err: any) => {
      this.createMessage('Something went wrong', 'Error');
      this.isLoading.set(false);
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  createMessage(message, type) {
    this.uiService.displayMessage(type, message);
  }

  updateCanSave() {
    let canSave = true;
    this.creditDebitReasonsTable.forEach(a => {
      if (a.Reason?.length && (a.enumARReasonCreditAdjustmentID || a.enumARReasonDebitAdjustmentID)) {
        // all good
      }
      else {
        canSave = false;
      }
    });
    this.canSaveValue = canSave;
  }

  saveChanges() {
    // send post
    this.isLoading.set(true);
    this.SaveCreditDebitReasons().then((success: DataARCreditDebitReasonModel[]) => {
      this.isLoading.set(false);
      if (success) {
        this.createMessage('Reasons Saved', 'Success');
        // this.creditDebitReasonsTable = success;
        // if (this.showCreditReasons) {
        //   this.creditDebitReasonsTable = this.creditDebitReasons.filter(a => a.enumCreditDebitTypeID === this.enumCreditDebitTypeCredit);
        // } else {
        //   this.creditDebitReasonsTable = this.creditDebitReasons.filter(a => a.enumCreditDebitTypeID === this.enumCreditDebitTypeDebit);
        // }

        // close window for now
        this.dialogRef.close(true);
      }
      else {
        this.createMessage('Reasons Not Saved', 'Error');
      }
    }).catch((err: any) => {
      this.createMessage('Error saving reasons', 'Error');
      this.isLoading.set(false);
    }).finally(() => {
      this.isLoading.set(false);
    });
  }

  addRow() {
    const row = new DataARCreditDebitReasonModel();
    row.LOCID = this.LOCID;
    row.Reason = '';
    row.enumARReasonCreditAdjustmentID = null;
    row.enumARReasonDebitAdjustmentID = null;
    row.enumCreditDebitTypeID = this.showDebitReasons ? this.enumCreditDebitTypeDebit : this.enumCreditDebitTypeCredit;
    row.isExcludeFromAdjRec = false;
    this.creditDebitReasonsTable.push(row);

    // update table
    // this.tdoTable.updateData();
    this.table.renderRows();
    this.updateCanSave();
  }

  removeReason(CreditDebitReasonID: string) {
    // set xauditdeleted to true
    this.creditDebitReasonsTable.forEach(t => {
      if (t.CreditDebitReasonID === CreditDebitReasonID) {
        t.xauditDeleted = true;
        this.creditDebitReasonsRemoved.push(t);
      }
    });

    // reload data and table
    this.creditDebitReasonsTable = this.creditDebitReasonsTable.filter(a => a.CreditDebitReasonID !== CreditDebitReasonID);
    this.table.renderRows();
    this.updateCanSave();
  }

  // DeleteCreditDebitReason(CreditDebitReasonID: string) {
  //   const promise = new Promise((resolve, reject) => {
  //     this.arService.DeleteCreditDebitReason(CreditDebitReasonID)
  //       .subscribe(resolve, reject);
  //   });
  //   return promise;
  // }

  SaveCreditDebitReasons() {

    // first append any deleted records to be sent to server
    this.creditDebitReasonsRemoved.forEach(a => {
      this.creditDebitReasonsTable.push(a);
    });

    const promise = new Promise((resolve, reject) => {
      this.arService.SaveCreditDebitReasons(this.creditDebitReasonsTable)
        .subscribe(resolve, reject);
    });
    return promise;
  }
}
